import { useEffect, useRef, useState } from "react";
import { WifiOff, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Global API health banner.
 *
 * Listens to `bula:api-failed` (emitted by the axios response interceptor on
 * CORS / network errors) and `bula:api-recovered` (emitted on any successful
 * response). Shows itself only after 2 consecutive failures within a 30s
 * window so a single flaky request doesn't scare users; hides immediately on
 * the next success.
 *
 * Renders nothing during normal operation (zero footprint).
 */
const FAIL_THRESHOLD = 2;
const FAIL_WINDOW_MS = 30_000;

export default function ApiHealthBanner() {
    const [degraded, setDegraded] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const failTimestampsRef = useRef([]);

    useEffect(() => {
        const onFail = () => {
            const now = Date.now();
            failTimestampsRef.current = failTimestampsRef.current.filter(
                (t) => now - t < FAIL_WINDOW_MS
            );
            failTimestampsRef.current.push(now);
            if (failTimestampsRef.current.length >= FAIL_THRESHOLD) {
                setDegraded(true);
            }
        };
        const onRecover = () => {
            failTimestampsRef.current = [];
            setDegraded(false);
            setRetrying(false);
        };
        window.addEventListener("bula:api-failed", onFail);
        window.addEventListener("bula:api-recovered", onRecover);
        return () => {
            window.removeEventListener("bula:api-failed", onFail);
            window.removeEventListener("bula:api-recovered", onRecover);
        };
    }, []);

    const retry = async () => {
        setRetrying(true);
        try {
            await api.get("/");
            // Interceptor will fire onRecover. As a safety net:
            failTimestampsRef.current = [];
            setDegraded(false);
        } catch {
            // Still failing — interceptor will re-fire onFail.
        } finally {
            setRetrying(false);
        }
    };

    if (!degraded) return null;

    return (
        <div
            data-testid="api-health-banner"
            role="alert"
            className="w-full bg-coral text-white border-b border-coral-deep/40"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3 flex-wrap">
                <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
                <p className="font-app text-xs sm:text-sm leading-snug flex-1 min-w-0">
                    <span className="font-bold">API offline</span>
                    <span className="opacity-90"> · we can't reach our servers right now. Some pages may render with limited data. </span>
                    <a
                        href="mailto:info@bularealestatefiji.com"
                        className="underline underline-offset-2 font-semibold hover:opacity-90"
                        data-testid="api-health-banner-contact"
                    >
                        Contact admin
                    </a>
                </p>
                <button
                    type="button"
                    onClick={retry}
                    disabled={retrying}
                    data-testid="api-health-banner-retry"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/15 hover:bg-white/25 disabled:opacity-60 px-3 py-1 text-[11px] font-app font-semibold uppercase tracking-wider transition-colors"
                >
                    {retrying ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                        <RefreshCw className="h-3 w-3" />
                    )}
                    {retrying ? "Retrying" : "Retry"}
                </button>
            </div>
        </div>
    );
}
